import { defineMessage, type IntlShape } from 'react-intl';
import { formatTime, parseTime } from '~/ui/formatted';
import { daySpan, isSameDayUTC, weekSpan } from './span';

/**
 * Narrow session shape consumed by `<EventTime>` and `formatEventTime`.
 * The full `EventSession` from `~/domain/availability/types` is assignable to
 * this — we only need the two time fields.
 */
export type EventSession = {
  startTime: string;
  endTime?: string | null;
};

/** Discriminator describing how a sessions array should be rendered. */
export type EventTimeKind =
  | 'single-day-single'
  | 'multi-day-single'
  | 'single-day-multi'
  | 'short-multi'
  | 'long-multi';

const DATE_WEEKDAY_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
};

const DATE_NO_WEEKDAY_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
};

// Multi-day-single title parts ("13 Aug"). Short month and no year keep the
// combined date+time range scannable — the design reference is
// "13 Aug 10:30 – 18 Aug 10:30" (US-11023). Exported: the open flow's
// checkout footer reuses this convention for its own "13 Aug" day label.
export const DATE_DAY_SHORT_MONTH_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
};

// Per-session row options (see `formatSessionDateTime`). No year — a row sits
// inside a card whose title already establishes it.
const SESSION_ROW_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
};

const SESSION_ROW_RANGE_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
};

// i18n descriptors are declared at module top so the formatjs extractor sees
// them. Inline `defineMessage` inside a function body wouldn't be picked up by
// `intl:extract`.
const STARTS_DATE_MSG = defineMessage<{ date: string }>({
  defaultMessage: 'Starts {date}',
  description:
    'EventTime title for multi-session events showing the first session date',
});

const SESSIONS_AND_WEEKS_MSG = defineMessage<{ count: number; span: number }>({
  defaultMessage:
    '{count, plural, one {# session} other {# sessions}}, {span, plural, one {# week} other {# weeks}}',
  description:
    'EventTime details for multi-session events spanning more than a week',
});

const SESSIONS_AND_DAYS_MSG = defineMessage<{ count: number; span: number }>({
  defaultMessage:
    '{count, plural, one {# session} other {# sessions}}, {span, plural, one {# day} other {# days}}',
  description:
    'EventTime details for multi-session events spanning a week or less',
});

/**
 * Formats just the time portion of a range as `"HH:MM – HH:MM"` (or `"HH:MM"`
 * when there's no end). Used by callers that have already chosen to display
 * the range's date separately — we render the times alone so consecutive
 * days don't bleed the date back in (which `Intl.formatRange` would otherwise
 * do for a cross-day range).
 */
function formatTimeRange(
  locale: string,
  start: Date,
  end: Date | null
): string {
  const startStr = formatTime(locale, start);
  if (!end) return startStr;
  const endStr = formatTime(locale, end);
  return `${startStr} – ${endStr}`;
}

/**
 * Whether a time string carries a time of day at all. A date-only value like
 * `"2026-08-15"` — what the open flow's time step has before the visitor picks
 * a slot — parses to UTC midnight, so formatting its range would render a
 * meaningless "00:00" as if that were the start time. Callers drop the details
 * line for those instead.
 */
function hasTimeOfDay(value: string): boolean {
  // Unix-ms timestamps (the other shape `parseTime` accepts) always carry one.
  return /^\d+$/.test(value) || value.includes('T');
}

/**
 * Derives the rendering kind for an event from its sessions array. Drives
 * `formatEventTime` and any caller that needs to branch on the resulting
 * structure (e.g. styling tweaks).
 *
 * See the Phase 1c spec in `.plans/home-page.md` for the rules table.
 */
export function deriveEventTimeKind(sessions: EventSession[]): EventTimeKind {
  if (sessions.length === 0) {
    // No sessions is a degenerate case — fall through to single-day-single so
    // callers don't have to special-case it. The render path below renders an
    // empty title rather than crashing.
    return 'single-day-single';
  }

  if (sessions.length === 1) {
    const session = sessions[0]!;
    const start = parseTime(session.startTime);
    const end = session.endTime ? parseTime(session.endTime) : null;
    if (!end || isSameDayUTC(start, end)) return 'single-day-single';
    return 'multi-day-single';
  }

  // 2+ sessions. Same-day-multi when every session shares the same UTC day
  // as the first.
  const firstStart = parseTime(sessions[0]!.startTime);
  const allSameDay = sessions.every((s) =>
    isSameDayUTC(parseTime(s.startTime), firstStart)
  );
  if (allSameDay) return 'single-day-multi';

  const days = daySpan(sessions);
  return days > 7 ? 'long-multi' : 'short-multi';
}

/**
 * Whether a session spans more than one UTC day — its end falls on a later
 * calendar day than its start. Drives the "Session details" rows: a multi-day
 * session carries its times inline with each date (see `formatSessionDateTime`),
 * so the row must not also render a separate time range below it.
 */
export function isMultiDaySession(session: EventSession): boolean {
  if (!session.endTime) return false;
  return !isSameDayUTC(
    parseTime(session.startTime),
    parseTime(session.endTime)
  );
}

/**
 * Formats the "when" label for a **single** session — the per-session rows in
 * the "Session details" list, where each row carries its own date.
 *
 * A session confined to one UTC day renders as the date alone
 * (`"Friday 5 September"`); the row shows its time range on a separate line
 * below. A session that spans days renders each date next to its own time
 * (`"5 September 10:00 – 6 September 16:00"`) so the reader can see when it both
 * starts and finishes — rendering only the start date made a multi-day session
 * read as a single-day booking, since a separate time-only line
 * (`"10:00 – 16:00"`) can't reveal that the end falls on a later date
 * (US-10838).
 *
 * The span form drops the weekday, matching the `multi-day-single` title
 * convention in `formatEventTime` above — two weekdays in one label crowds the
 * row for no gain.
 */
export function formatSessionDateTime(
  session: EventSession,
  intl: IntlShape
): string {
  const start = parseTime(session.startTime);
  const end = session.endTime ? parseTime(session.endTime) : null;
  if (!end || isSameDayUTC(start, end)) {
    return intl.formatDate(start, SESSION_ROW_OPTS);
  }
  const startLabel = `${intl.formatDate(start, SESSION_ROW_RANGE_OPTS)} ${formatTime(intl.locale, start)}`;
  const endLabel = `${intl.formatDate(end, SESSION_ROW_RANGE_OPTS)} ${formatTime(intl.locale, end)}`;
  return `${startLabel} – ${endLabel}`;
}

/**
 * Pure formatter for an event's date/time row. Takes the sessions array plus a
 * react-intl `IntlShape` (so it can be used both inside React components via
 * `useIntl()` and in tests without rendering). Returns the `title` and
 * optional `details` strings the `<EventTime>` component renders.
 *
 * Always renders in UTC — see `~/ui/formatted` `parseTime` for the rationale.
 */
export function formatEventTime(
  sessions: EventSession[],
  intl: IntlShape
): { title: string; details: string | null } {
  const kind = deriveEventTimeKind(sessions);

  if (sessions.length === 0) {
    return { title: '', details: null };
  }

  switch (kind) {
    case 'single-day-single': {
      const session = sessions[0]!;
      const start = parseTime(session.startTime);
      const end = session.endTime ? parseTime(session.endTime) : null;
      const title = intl.formatDate(start, DATE_WEEKDAY_OPTS);
      const details = hasTimeOfDay(session.startTime)
        ? formatTimeRange(intl.locale, start, end)
        : null;
      return { title, details };
    }

    case 'multi-day-single': {
      const session = sessions[0]!;
      const start = parseTime(session.startTime);
      const end = parseTime(session.endTime!);
      // "13 Aug 10:30 – 18 Aug 10:30" — each date next to its own time. A
      // date range with a separate time pair ("13 – 18 August 2026 ·
      // 10:30 – 10:30") read as a zero-length time range (US-11023); same
      // reasoning as formatSessionDateTime below (US-10838).
      const startLabel = `${intl.formatDate(start, DATE_DAY_SHORT_MONTH_OPTS)} ${formatTime(intl.locale, start)}`;
      const endLabel = `${intl.formatDate(end, DATE_DAY_SHORT_MONTH_OPTS)} ${formatTime(intl.locale, end)}`;
      return { title: `${startLabel} – ${endLabel}`, details: null };
    }

    case 'single-day-multi': {
      // First start through latest end on the same UTC day. Use the maximum
      // endTime across all sessions rather than the last session's endTime —
      // sessions may be ordered such that a non-last session ends later than
      // the final one (e.g. a long workshop overlapping a shorter slot). Fall
      // back to the latest startTime when no session has an endTime.
      const firstStart = parseTime(sessions[0]!.startTime);
      const endTimes = sessions
        .filter((s): s is EventSession & { endTime: string } => !!s.endTime)
        .map((s) => parseTime(s.endTime).getTime())
        .filter((n) => !Number.isNaN(n));
      let latestEnd: Date | null;
      if (endTimes.length > 0) {
        latestEnd = new Date(Math.max(...endTimes));
      } else {
        const startTimes = sessions
          .map((s) => parseTime(s.startTime).getTime())
          .filter((n) => !Number.isNaN(n));
        const latestStart = startTimes.length > 0 ? Math.max(...startTimes) : 0;
        // If only one valid start (or all the same), keep latestEnd null so
        // `formatTimeRange` renders the start alone — no degenerate "X – X".
        latestEnd =
          latestStart > firstStart.getTime() ? new Date(latestStart) : null;
      }
      const title = intl.formatDate(firstStart, DATE_WEEKDAY_OPTS);
      // A single session with a real time is enough to make the range mean
      // something; only drop it when every session is date-only.
      const details = sessions.some((s) => hasTimeOfDay(s.startTime))
        ? formatTimeRange(intl.locale, firstStart, latestEnd)
        : null;
      return { title, details };
    }

    case 'short-multi':
    case 'long-multi': {
      const first = sessions[0]!;
      const startsDate = intl.formatDate(
        parseTime(first.startTime),
        DATE_NO_WEEKDAY_OPTS
      );
      const title = intl.formatMessage(STARTS_DATE_MSG, { date: startsDate });

      const count = sessions.length;
      const details =
        kind === 'long-multi'
          ? intl.formatMessage(SESSIONS_AND_WEEKS_MSG, {
              count,
              span: weekSpan(sessions),
            })
          : intl.formatMessage(SESSIONS_AND_DAYS_MSG, {
              count,
              span: daySpan(sessions),
            });
      return { title, details };
    }
  }
}
